import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import footer_logo from "@/assets/images/footer_logo.png";
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const navLinks = [
    { label: "Home", path: "/" },
    { label: "Products", path: "/products" },
    { label: "About", path: "/about" },
    { label: "Contact", path: "/contact" },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isHomePage = location.pathname === "/";
  // On non-home pages, navbar always behaves as "scrolled" (white bg, dark text)
  const isDark = isHomePage && !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isDark ? "bg-transparent" : "bg-white/95 backdrop-blur-md shadow-sm"
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 lg:h-24 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <div className={`flex items-center justify-center rounded-xl px-4 py-2 transition-all duration-300 ${isDark ? "bg-white/90 backdrop-blur-sm" : "bg-white"}`}>
            <img
              src={footer_logo}
              alt="Gift Mantra Logo"
              className="h-12 lg:h-14 w-auto object-contain"
            />
          </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`relative text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${isActive(link.path)
                ? isDark
                  ? "text-white font-semibold"
                  : "text-transparent bg-clip-text bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]"
                : isDark
                  ? "text-white/90 hover:text-white"
                  : "text-gray-700 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#3a8cbf] hover:to-[#e05a8a]"
                }`}
            >
              {link.label}
              {isActive(link.path) && (
                <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] rounded-full" />
              )}
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-4">
          <Link
            to="/products"
            className="px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] hover:opacity-90 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Shop Now
          </Link>
        </div>

        {/* Hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${isDark ? "text-white" : "text-gray-700"} ${menuOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden bg-white/95 backdrop-blur-md ${menuOpen ? "max-h-64 border-t border-gray-100" : "max-h-0"
          }`}
      >
        <div className="px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-base font-medium py-1 cursor-pointer ${isActive(link.path)
                ? "text-transparent bg-clip-text bg-gradient-to-r from-[#5FA8D3] to-[#F78FB3]"
                : "text-gray-800"
                }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/products"
            className="mt-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-center cursor-pointer"
          >
            Shop Now
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
